import {
  Divider,
  Typography,
} from '@mui/joy';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import {
  SideTabMenu,
  LocalizationFeature,
  WarningChip,
  SaveCancelButtons,
  useLocalizationForm,
} from './MultiLanguageModal';

import { useTenantSlug } from '../tenant/helpers';

import { InputField } from '~/components/ui/input';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { LocalizationService } from '~/services/api/localization/endpoints';
import QueryResolver from '~/services/api/query-resolver';
import { useWrappedMutation } from '~/services/api/rest-api';
import { getErrorMessage } from '~/services/api/rest-api-error';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';
import { useForm } from '~/utils/forms';

export function TranslateCaseManager() {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();

  const form = useLocalizationForm();
  const isOriginal = form.watch('is_original');
  const selectedLanguage = form.watch('selected_language');
  const selectedFeature = form.watch('selected_feature');
  const selectedCaseManagerField = form.watch('selected_case_manager_field');
  const isEditing = form.watch('is_editing');
  const setSelectedFeature = (val: LocalizationFeature) =>
    form.setValue('selected_feature', val);
  const setSelectedCaseManagerField = (val: number) =>
    form.setValue('selected_case_manager_field', val);

  const localizedCaseManagerFieldsQuery =
    LocalizationService.endpoints.caseManagerFields.useQuery(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
      },
      { skip: !slug || !selectedChannel },
    );

  if (!selectedLanguage)
    return (
      <div className='flex justify-center'>
        <FormattedMessage defaultMessage='To view the translation of a language, kindly select a language first' />
      </div>
    );

  return (
    <div className='flex flex-row gap-4'>
      <div className='basis-96 flex flex-col gap-4 overflow-y-auto max-h-[700px] pr-2'>
        <WarningChip>
          <FormattedMessage defaultMessage="Changes made on the translated version won't affect the original content" />
        </WarningChip>

        <QueryResolver query={localizedCaseManagerFieldsQuery}>
          {(caseManagerFields) =>
            caseManagerFields.map((field) => (
              <SideTabMenu
                key={field.id}
                title={field.title}
                description={intl.formatMessage({
                  defaultMessage:
                    'View and make changes to the translated version of the added case action field',
                })}
                isActive={
                  selectedFeature === LocalizationFeature.CASE_MANAGER_FIELD &&
                  selectedCaseManagerField === field.id
                }
                disabled={isEditing}
                onClick={() => {
                  setSelectedFeature(LocalizationFeature.CASE_MANAGER_FIELD);
                  setSelectedCaseManagerField(field.id);
                }}
              />
            ))
          }
        </QueryResolver>
      </div>
      <Divider orientation='vertical' />
      <div className='flex-1 h-[700px]'>
        <QueryResolver query={localizedCaseManagerFieldsQuery}>
          {(caseManagerFields) => {
            return caseManagerFields.map((field) => {
              if (
                selectedFeature === LocalizationFeature.CASE_MANAGER_FIELD &&
                selectedCaseManagerField === field.id
              ) {
                let translatedData = undefined;
                Object.entries(field.translations).forEach(
                  ([, value]) => {
                    if (value.language_id === selectedLanguage) {
                      translatedData = value;
                    }
                  },
                );
                if (!translatedData)
                  translatedData = {
                    title: '',
                    dropdown_choices: [],
                    pre_defined_dropdown_choices: [],
                    id: null,
                  };
                return (
                  <CaseManagerFieldLocalization
                    key={`${selectedLanguage}-${isOriginal}-${selectedCaseManagerField}`}
                    defaultValues={{
                      language_id: selectedLanguage,
                      data: {
                        id: translatedData.id,
                        main_id: field.id,
                        dropdown_choices: field.dropdown_choices,
                        pre_defined_dropdown_choices:
                          field.pre_defined_dropdown_choices,
                        title: field.title,
                        translated_dropdown_choices:
                          translatedData.dropdown_choices,
                        translated_pre_defined_dropdown_choices:
                          translatedData.pre_defined_dropdown_choices,
                        translated_title: translatedData.title,
                      },
                    }}
                  />
                );
              }
              return null;
            });
          }
          }
        </QueryResolver>
      </div>
    </div>
  );
}

const caseManagerFieldSchema = z.object({
  language_id: z.number(),
  data: z.object({
    id: z.number().nullable(),
    main_id: z.number(),
    title: z.string(),
    dropdown_choices: z.array(z.string()),
    pre_defined_dropdown_choices: z.array(z.string()),
    translated_dropdown_choices: z.array(z.string()),
    translated_pre_defined_dropdown_choices: z.array(z.string()),
    translated_title: z.string(),
  }),
});

type CaseManagerFieldLocalizationData = z.infer<typeof caseManagerFieldSchema>;

function CaseManagerFieldLocalization({
  defaultValues,
}: {
  readonly defaultValues: CaseManagerFieldLocalizationData;
}) {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();
  const { createToastNotification } = useToastNotificationContext();
  const updateCaseManagerField = useWrappedMutation(
    LocalizationService.endpoints.updateCaseManagerFieldTranslation.useMutation(),
  );

  const localizedForm = useLocalizationForm();
  const isEditing = localizedForm.watch('is_editing');
  const isOriginal = localizedForm.watch('is_original');
  const setIsEditing = () => localizedForm.setValue('is_editing', !isEditing);

  const form = useForm({
    defaultValues,
    schema: caseManagerFieldSchema,
  });

  const onSubmitHandler = form.handleSubmit(async (formData) => {
    if (formData.data.id === null) return;
    await updateCaseManagerField.mutateAsync(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
        payload: {
          id: formData.data.id,
          dropdown_choices: formData.data.translated_dropdown_choices,
          pre_defined_dropdown_choices: formData.data.translated_pre_defined_dropdown_choices,
          title: formData.data.translated_title,
          main_id: formData.data.main_id,
          language_id: formData.language_id,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'data updated successfully' }),
            'success',
          );
          setIsEditing();
        },
        onError: (error) => {
          const customError = getErrorMessage(error);
          createToastNotification(customError, 'error');
        },
      },
    );
  });

  return (
    <div>
      <SaveCancelButtons
        isLoading={updateCaseManagerField.state.isLoading}
        onSave={onSubmitHandler}
        isEditable={defaultValues.data.id !== null}
      />
      <div className='flex flex-col gap-2 mt-8'>
        <Typography level='title-sm'>
          <FormattedMessage defaultMessage='Title' />
        </Typography>
        {isEditing ? (
          <InputField control={form.control} field='data.translated_title' />
        ) : (
          <Typography>
            {isOriginal
              ? form.watch('data.title')
              : form.watch('data.translated_title')}
          </Typography>
        )}
      </div>
    </div>
  );
}
